type CsvScalar = string | number | boolean | null | undefined;

export type CsvRow = Record<string, CsvScalar>;

const dateLike = (value: any): string | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
  }
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000).toISOString();
  return null;
};

export function csvValue(value: unknown): CsvScalar {
  if (value == null) return '';
  const date = dateLike(value);
  if (date) return date;
  if (Array.isArray(value)) return value.map(item => {
    const converted = csvValue(item);
    return typeof converted === 'string' ? converted : String(converted ?? '');
  }).join(' | ');
  if (typeof value === 'object') return JSON.stringify(value, (_key, child) => dateLike(child) || child);
  if (['string', 'number', 'boolean'].includes(typeof value)) return value as CsvScalar;
  return String(value);
}

export function flattenForCsv(
  value: Record<string, unknown> | null | undefined,
  prefix: string,
  blockedKeys: Set<string> = new Set()
): CsvRow {
  const row: CsvRow = {};
  if (!value) return row;
  Object.entries(value).forEach(([key, child]) => {
    if (blockedKeys.has(key) || key.toLowerCase().startsWith('legacy')) return;
    const column = prefix ? `${prefix}.${key}` : key;
    const date = dateLike(child);
    if (date) row[column] = date;
    else if (child && typeof child === 'object' && !Array.isArray(child)) {
      Object.assign(row, flattenForCsv(child as Record<string, unknown>, column, blockedKeys));
    } else row[column] = csvValue(child);
  });
  return row;
}

const escapeCell = (value: CsvScalar): string => {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function rowsToCsv(rows: CsvRow[]): string {
  const headers: string[] = [];
  const seen = new Set<string>();
  rows.forEach(row => Object.keys(row).forEach(key => {
    if (!seen.has(key)) { seen.add(key); headers.push(key); }
  }));
  return `\uFEFF${headers.map(escapeCell).join(',')}\r\n${rows.map(row => headers.map(key => escapeCell(row[key])).join(',')).join('\r\n')}`;
}

export function downloadCsv(filename: string, rows: CsvRow[]): void {
  const blob = new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
